// SCR-CONSOLE-002 (تب عملیات — AP-PLT-05): شارژ اعتبار پیامک مستأجر با دلیل (US-PLT-026؛ platform.sms_credit.manage —
// C-AGENT-414) و درخواست دسترسی پشتیبانی زمان‌دار (US-PLT-081؛ دلیل، ۱ تا ۴۸ ساعت، «شامل داده بالینی» صریح؛ اضطراری فقط
// platform-admin — C-AGENT-421). هیچ داده مراجعه‌کننده‌ای نمایش داده نمی‌شود.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type ConsoleTenantDetail } from '@phoenix/api-client';
import { usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, faDigits, KeyValueList, latinDigits, LoadingState } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PLATFORM_ROLES, usePlatformAccess } from '../../access';
import { consoleKeys, toNumber } from '../../api';
import { SupportGrantTable } from '../support/SupportAccessPage';

function SmsCreditCard({ tenant }: { tenant: ConsoleTenantDetail }) {
  const { t } = useTranslation('console');
  const { api } = usePlatformSession();
  const access = usePlatformAccess();
  const queryClient = useQueryClient();
  const [parts, setParts] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const credit = useQuery({
    queryKey: consoleKeys.smsCredit(tenant.id),
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/platform/console/tenants/{id}/sms-credit', { params: { path: { id: tenant.id } } }),
      ),
  });
  const charge = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/platform/console/tenants/{id}/sms-credit:charge', {
          params: { path: { id: tenant.id } },
          body: { parts: Number(latinDigits(parts)), reason: reason.trim() },
        }),
      ),
    onSuccess: async () => {
      setParts('');
      setReason('');
      setTouched(false);
      await queryClient.invalidateQueries({ queryKey: consoleKeys.smsCredit(tenant.id) });
      await queryClient.invalidateQueries({ queryKey: consoleKeys.tenant(tenant.id) });
    },
  });

  const partsValue = Number(latinDigits(parts));
  const partsInvalid = !Number.isInteger(partsValue) || partsValue <= 0 || partsValue > 10_000_000;
  const reasonInvalid = reason.trim().length < 3;
  const balance = credit.data?.balance;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="sms-credit-card">
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t('console:operations.sms_title')}
      </Typography>
      {credit.isPending ? (
        <LoadingState rows={1} />
      ) : credit.error ? (
        <ErrorState error={credit.error} compact />
      ) : balance ? (
        <KeyValueList
          columns={3}
          items={[
            {
              labelKey: 'console:operations.balance',
              value: <bdi dir="ltr">{faDigits(toNumber(balance.balance) ?? 0)}</bdi>,
              testId: 'console-sms-balance',
            },
            {
              labelKey: 'console:operations.negative_cap',
              value: faDigits(toNumber(balance.negativeCap) ?? 0),
            },
            {
              labelKey: 'console:operations.marketing',
              value: balance.marketingAllowed ? t('console:yes') : t('console:no'),
            },
          ]}
        />
      ) : null}
      {access.can('platform.sms_credit.manage') && (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ mt: 2 }}
          alignItems={{ md: 'flex-start' }}
        >
          <TextField
            label={t('console:operations.charge_parts')}
            value={parts}
            onChange={(event) => setParts(event.target.value)}
            error={touched && partsInvalid}
            helperText={touched && partsInvalid ? t('console:operations.parts_invalid') : undefined}
            slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'charge-parts' } }}
          />
          <TextField
            label={t('console:operations.charge_reason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            error={touched && reasonInvalid}
            helperText={touched && reasonInvalid ? t('console:validation.reason_min') : undefined}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'charge-reason' } }}
          />
          <Button
            variant="contained"
            onClick={() => {
              setTouched(true);
              if (!partsInvalid && !reasonInvalid) {
                charge.mutate();
              }
            }}
            disabled={charge.isPending}
            data-testid="charge-submit"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('console:operations.charge')}
          </Button>
        </Stack>
      )}
      {charge.error ? <ErrorState error={charge.error} compact /> : null}
      {charge.isSuccess && (
        <Alert severity="success" sx={{ mt: 1 }} role="status" data-testid="charge-done">
          {t('console:operations.charged')}
        </Alert>
      )}
    </Paper>
  );
}

function SupportAccessCard({ tenant }: { tenant: ConsoleTenantDetail }) {
  const { t } = useTranslation('console');
  const { api, hasPlatformRole } = usePlatformSession();
  const queryClient = useQueryClient();
  const isAdmin = hasPlatformRole([PLATFORM_ROLES.admin]);
  const [reason, setReason] = useState('');
  const [hours, setHours] = useState('2');
  const [clinical, setClinical] = useState(false);
  const [emergency, setEmergency] = useState(false);
  const [touched, setTouched] = useState(false);

  const request = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/platform/console/tenants/{id}/support-access', {
          params: { path: { id: tenant.id } },
          body: {
            reason: reason.trim(),
            durationHours: Number(latinDigits(hours)),
            includesClinical: clinical,
            emergency: isAdmin && emergency,
          },
        }),
      ),
    onSuccess: async () => {
      setReason('');
      setTouched(false);
      await queryClient.invalidateQueries({ queryKey: consoleKeys.supportAccessRoot });
    },
  });

  const duration = Number(latinDigits(hours));
  const durationInvalid = !Number.isInteger(duration) || duration < 1 || duration > 48;
  const reasonInvalid = reason.trim().length < 3;
  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="support-access-card">
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        {t('console:support_access.request_title')}
      </Typography>
      <Stack spacing={2}>
        <TextField
          label={t('console:support_access.reason')}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          error={touched && reasonInvalid}
          helperText={touched && reasonInvalid ? t('console:validation.reason_min') : undefined}
          fullWidth
          multiline
          minRows={2}
          slotProps={{ htmlInput: { maxLength: 500, 'data-testid': 'support-reason' } }}
        />
        <TextField
          label={t('console:support_access.duration')}
          value={hours}
          onChange={(event) => setHours(event.target.value)}
          error={touched && durationInvalid}
          helperText={
            durationInvalid
              ? t('console:support_access.duration_invalid')
              : t('console:support_access.duration_help')
          }
          sx={{ maxWidth: 240 }}
          slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', 'data-testid': 'support-hours' } }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={clinical}
              onChange={(event) => setClinical(event.target.checked)}
              data-testid="support-clinical"
            />
          }
          label={t('console:support_access.clinical')}
        />
        {isAdmin && (
          <FormControlLabel
            control={
              <Checkbox
                checked={emergency}
                onChange={(event) => setEmergency(event.target.checked)}
                data-testid="support-emergency"
              />
            }
            label={t('console:support_access.emergency')}
          />
        )}
        {request.error ? <ErrorState error={request.error} compact /> : null}
        {request.isSuccess && (
          <Alert severity="success" role="status" data-testid="support-requested">
            {t('console:support_access.requested')}
          </Alert>
        )}
        <Stack direction="row">
          <Button
            variant="contained"
            onClick={() => {
              setTouched(true);
              if (!durationInvalid && !reasonInvalid) {
                request.mutate();
              }
            }}
            disabled={request.isPending}
            data-testid="support-request"
          >
            {t('console:support_access.request')}
          </Button>
        </Stack>
      </Stack>
      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        {t('console:support_access.tenant_grants')}
      </Typography>
      <SupportGrantTable tenantId={tenant.id} />
    </Paper>
  );
}

export function OperationsTab({ tenant }: { tenant: ConsoleTenantDetail }) {
  const access = usePlatformAccess();
  return (
    <Stack spacing={2} data-testid="operations-tab">
      <SmsCreditCard tenant={tenant} />
      {access.can('platform.support_access.request') && <SupportAccessCard tenant={tenant} />}
    </Stack>
  );
}
